import { ConfigProvider, theme as antdTheme } from 'antd';
import ru_RU from 'antd/lib/locale/ru_RU';

import { appColors } from '~app/styles/colors';
import {
  useDisablePinchZoom,
  useIsMobileAuth,
  useResolvedMobileAuthTheme,
} from '~shared/lib/mobile-auth';
import { useWindowInnerWidth } from '~shared/ui/utils';

import { AppProps } from '../types';

const MOBILE_BREAKPOINT = 768;
const MOBILE_INPUT_HEIGHT = 56;

const mobileAuthPalette = {
  light: {
    primary: '#1560bd',
    primaryHover: '#0f4a8f',
    secondary: '#00baad',
    text: '#111827',
    textSecondary: '#4b5563',
    placeholder: '#62748E',
    border: '#e5e7eb',
    disabledBg: '#f3f4f6',
    disabledText: '#62748E',
    error: '#dc2626',
    bgContainer: '#ffffff',
  },
  dark: {
    primary: '#5b9bff',
    primaryHover: '#7fb2ff',
    secondary: '#2dd4bf',
    text: '#ffffff',
    textSecondary: '#c2c6cd',
    placeholder: '#8a8f97',
    border: '#2a2d33',
    disabledBg: '#0d0e10',
    disabledText: '#6b7280',
    error: '#f87171',
    bgContainer: '#1a1c20',
  },
};

export const withConfigProvider = (component: Component) => (props: AppProps) => {
  const isMobileAuth = useIsMobileAuth();
  const resolvedTheme = useResolvedMobileAuthTheme();

  useDisablePinchZoom();
  const isDark = resolvedTheme === 'dark';
  const ma = isMobileAuth || isDark ? mobileAuthPalette[resolvedTheme] : null;
  const windowWidth = useWindowInnerWidth();
  const isMobile = windowWidth <= MOBILE_BREAKPOINT;
  const inputHeight = isMobile ? MOBILE_INPUT_HEIGHT : undefined;

  const themeConfig = {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    components: {
      Segmented: {
        itemSelectedBg: ma?.primary ?? appColors.primary,
        itemSelectedColor: appColors.white,
        itemColor: ma?.text ?? appColors.black,
      },
      Button: {
        fontSize: 15,
        colorBgContainerDisabled: ma?.disabledBg ?? appColors.disabledBg,
        colorTextDisabled: ma?.disabledText ?? appColors.disabledText,
        borderRadiusLg: ma ? 16 : 15,
        borderRadiusSM: 8,
        defaultBg: appColors.content,
        defaultBorderColor: appColors.stroke,
        defaultColor: appColors.black,
        defaultHoverBg: appColors.content,
        defaultHoverBorderColor: appColors.primary,
        defaultHoverColor: appColors.primary,
        textHoverBg: appColors.bg,
      },
      List: {
        colorBorder: ma?.border ?? appColors.stroke,
      },
      Avatar: {
        colorTextPlaceholder: appColors.gray,
      },
      Tabs: {
        colorBorderSecondary: ma?.border ?? appColors.stroke,
        borderRadius: 0,
        borderRadiusLG: 0,
        cardGutter: -1,
        margin: 0,
      },
      Tag: {
        // fontSize: 12,
        // fontSizeIcon: 14,
        padding: 8,
        // fontSizeSM: 15,
        // borderRadiusSM: 0,
        marginXS: 0,
      },
      Input: {
        colorBgContainer: appColors.content,
        colorBgContainerDisabled: ma?.disabledBg ?? appColors.disabledBg,
        colorTextDisabled: ma?.disabledText ?? appColors.disabledText,
        colorTextPlaceholder: appColors.placeholder,
        colorBorder: appColors.stroke,
        fontSize: 15,
        ...(inputHeight ? { controlHeight: inputHeight, controlHeightLG: inputHeight } : {}),
      },
      Select: {
        colorBgContainerDisabled: ma?.disabledBg ?? appColors.disabledBg,
        colorTextDisabled: ma?.disabledText ?? appColors.disabledText,
        fontSize: 15,
        colorBgElevated: appColors.content,
        colorBorder: appColors.stroke,
        colorIcon: appColors.placeholder,
        colorIconHover: appColors.primary,
        colorTextPlaceholder: appColors.placeholder,
        optionSelectedBg: appColors.bg,
        ...(inputHeight ? { controlHeight: inputHeight, controlHeightLG: inputHeight } : {}),
      },
      DateRange: {
        colorIcon: ma?.primary ?? appColors.primary,
        colorBgContainer: appColors.content,
        colorBgContainerDisabled: ma?.disabledBg ?? appColors.disabledBg,
        colorTextDisabled: ma?.disabledText ?? appColors.disabledText,
        colorBgElevated: appColors.content,
        colorIconHover: appColors.primary,
        colorTextPlaceholder: appColors.placeholder,
        colorBorder: appColors.stroke,
        fontSize: 15,
        ...(inputHeight ? { controlHeight: inputHeight, controlHeightLG: inputHeight } : {}),
      },
      DatePicker: {
        colorIcon: ma?.primary ?? appColors.primary,
        colorBgContainer: appColors.content,
        colorBgContainerDisabled: ma?.disabledBg ?? appColors.disabledBg,
        colorTextDisabled: ma?.disabledText ?? appColors.disabledText,
        colorBgElevated: appColors.content,
        colorIconHover: appColors.primary,
        colorTextPlaceholder: appColors.placeholder,
        colorBorder: appColors.stroke,
        fontSize: 15,
        ...(inputHeight ? { controlHeight: inputHeight, controlHeightLG: inputHeight } : {}),
      },
      Table: {
        colorBgContainer: appColors.content,
        headerBg: appColors.content,
        headerColor: appColors.placeholder,
        headerSplitColor: 'transparent',
        borderColor: appColors.stroke,
        rowHoverBg: appColors.bg,
        cellPaddingBlock: 16,
        cellPaddingInline: 16,
        bodySortBg: appColors.content,
        headerSortActiveBg: appColors.content,
        headerSortHoverBg: appColors.content,
        fixedHeaderSortActiveBg: appColors.content,
      },

      Typography: {
        // fontFamilyCode: "'Poppins', 'sans-serif' ",
        fontSize: 15,
        lineHeight: 1.2,
        verticalAlign: 'center',
        colorLink: appColors.black,
        colorLinkHover: appColors.black,
        colorLinkActive: appColors.black,
      },
      Breadcrumb: {
        fontSize: 12,
      },
      Divider: {
        margin: 0,
      },
      Image: {
        colorBgMask: 'rgba(0, 0, 0, 0.7)',
        colorTextLightSolid: appColors.white,
        marginXL: 15,
      },
      Calendar: {
        fullPanelBg: isDark ? '#0d0e10' : '#F6F6F6',
        itemActiveBg: isDark ? 'rgba(91, 155, 255, 0.15)' : '#E9F2F7',
      },
      Card: {
        colorBorderSecondary: ma?.border ?? appColors.stroke,
        padding: 70,
        paddinLG: 16,
      },
    },
    token: {
      titleMarginTop: '0',
      titleMarginBottom: '0',
      colorFillAlter: ma?.bgContainer ?? appColors.white,
      borderRadius: ma ? 16 : 12,
      borderRadiusLG: ma ? 16 : 12,
      borderRadiusSM: ma ? 12 : 12,
      controlHeightLG: ma ? 48 : 46,
      controlHeight: ma ? 48 : 34,
      controlHeightSM: 28,
      controlPaddingHorizontal: 16,
      paddingSM: 16,
      paddingContentHorizontal: 16,
      colorPrimary: ma?.primary ?? appColors.primary,
      colorError: ma?.error ?? appColors.red,
      colorText: ma?.text ?? appColors.black,
      ...(ma ? { colorBgContainer: ma.bgContainer } : {}),

      colorBorder: ma?.border ?? appColors.stroke,
      colorTextPlaceholder: ma?.placeholder ?? appColors.placeholder,
      colorTextQuaternary: ma?.placeholder ?? appColors.placeholder,
      colorIconHover: ma?.primary ?? appColors.primary,
      colorTextDisabled: ma?.disabledText ?? appColors.disabledText,
    },
  };

  return (
    <ConfigProvider theme={themeConfig} locale={ru_RU}>
      {component(props)}
    </ConfigProvider>
  );
};
